import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { FEATURE } from '~/common/enums/feature.enum';
import useFeaturedContests from '~/domains/cms/promo/featured.contests/useFeaturedContests';
import { useContests } from '~/domains/contest/hooks/useContests';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import { useLocationKey } from '~/hooks/useLocationKey';

const useInvalidateContestsOnLocationChange = () => {
  const queryClient = useQueryClient();
  const { locationState, isLocationReady, hasLocationToken } = useLocationKey();
  const previousLocationStateRef = useRef<string | undefined>();

  useEffect(() => {
    const hasLocationChanged = previousLocationStateRef.current !== locationState;

    previousLocationStateRef.current = locationState;

    if (!hasLocationChanged || !isLocationReady || !hasLocationToken) return;

    queryClient.invalidateQueries({ queryKey: ['contests'] });
    queryClient.invalidateQueries({ queryKey: ['contest-section'] });
  }, [locationState, isLocationReady, hasLocationToken, queryClient]);
};

export const useHomeFeaturedContests = () => {
  const isShowContestThumbnailEnabled = useIsFeatureEnabled(FEATURE.SHOW_CONTEST_ITEM_THUMBNAIL);
  const { data: contestIds } = useFeaturedContests();

  const contestIdsLength = contestIds?.length ?? 0;

  useInvalidateContestsOnLocationChange();

  const data = useContests(
    {
      filter: { contestIds },
      limit: contestIdsLength,
      offset: 0,
    },
    {
      enabled: contestIdsLength > 0,
      keepPreviousData: true,
    }
  );

  return { data, isShowContestThumbnailEnabled };
};
